// src/pages/HomePage.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Heading,
  Text,
  Button,
  Flex,
  Card,
  Grid,
  Image,
  Divider
} from '@aws-amplify/ui-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import './HomePage.css';
import { Helmet } from 'react-helmet-async';
import { Film, Camera, Users, HardDrive, Shield } from 'lucide-react';

const HomePage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const fadeTimeout = useRef<NodeJS.Timeout | null>(null);

  // Replace with your actual S3 URLs
  const backgroundImages = [
    'https://d3cx43beuu8rpy.cloudfront.net/L1030861.jpg',
    'https://d3cx43beuu8rpy.cloudfront.net/2022091112082150-5530625228662070411-IMG_1534_NEF.jpg',
    'https://d3cx43beuu8rpy.cloudfront.net/2022091115194931--3914088275993598943-IMG_1745_NEF.jpg',
    'https://d3cx43beuu8rpy.cloudfront.net/2022091210431003--7560361470817383723-IMG_1585_NEF.jpg',
    'https://d3cx43beuu8rpy.cloudfront.net/DSC_0264.jpg',
    'https://d3cx43beuu8rpy.cloudfront.net/tulips.jpg',
    'https://d3cx43beuu8rpy.cloudfront.net/L1000311.jpg',
    'https://d3cx43beuu8rpy.cloudfront.net/L1060970.jpg',
    'https://d3cx43beuu8rpy.cloudfront.net/DSC_0601.jpg'
  ];

  // Crossfade effect between images
  useEffect(() => {
    if (fadeTimeout.current) {
      clearTimeout(fadeTimeout.current);
    }

    fadeTimeout.current = setTimeout(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % backgroundImages.length);
    }, 8000);

    return () => {
      if (fadeTimeout.current) {
        clearTimeout(fadeTimeout.current);
      }
    };
  }, [currentImageIndex, backgroundImages.length]);

  return (
    <View className="homepage-container">
      <Helmet>
        <title>Banjara - Capture Moments, Preserve Memories</title>
        <meta
          name="description"
          content="Private, secure photo albums for travelers and adventurers. Share memories with your companions."
        />
        <meta
          property="og:description"
          content="Securely capture, store, and share your memories with your companions."
        />
        <meta
          property="og:image"
          content="https://d3cx43beuu8rpy.cloudfront.net/Banjara.PNG"
        />
      </Helmet>

      {/* Hero Section with Parallax and Crossfade Effect */}
      <View className="hero-section">
        <div className="background-container">
          {backgroundImages.map((img, index) => (
            <div
              key={index}
              className={`background-image ${currentImageIndex === index ? 'active' : ''}`}
              style={{ backgroundImage: `url(${img})` }}
            />
          ))}
          <div className="overlay"></div>
        </div>

        <Flex
          direction="column"
          alignItems="center"
          justifyContent="center"
          padding="2rem"
          textAlign="center"
          className="hero-content"
        >
          <Text
            className="hero-tagline"
            fontSize="1.2rem"
            fontWeight="300"
            letterSpacing="0.2em"
            marginBottom="1.5rem"
          >
            MOMENTS FADE, MEMORIES REMAIN
          </Text>

          <Heading
            level={1}
            color="white"
            fontWeight="bold"
            className="hero-title"
            marginBottom="1.5rem"
          >
            Preserve Your Journey
          </Heading>

          <Text
            color="white"
            fontSize="1.25rem"
            marginBottom="2.5rem"
            maxWidth="700px"
            className="hero-description"
          >
            Some memories are too precious to forget. Capture life's fleeting moments
            and keep them safe in a space that only you and your companions can access.
          </Text>

          <Flex direction={{ base: 'column', medium: 'row' }} gap="1rem">
            <Button
              className="cta-button primary"
              size="large"
              onClick={() => navigate('/trips')}
            >
              Start Your Collection
            </Button>

            <Button
              className="cta-button secondary"
              size="large"
              onClick={() => {
                const featuresSection = document.getElementById('features');
                if (featuresSection) {
                  featuresSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
            >
              Explore Features
            </Button>
          </Flex>
        </Flex>
      </View>

      {/* Features Section */}
      <View id="features" className="features-section">
        <Flex direction="column" alignItems="center" marginBottom="3rem">
          <Heading
            level={2}
            color="white"
            fontWeight="bold"
            marginBottom="1rem"
            className="section-title"
          >
            Your Story
          </Heading>
          <Divider width="6rem" backgroundColor="#3B82F6" height="0.25rem" />
        </Flex>

        <Grid
          templateColumns={{ base: '1fr', medium: '1fr 1fr 1fr' }}
          gap="2rem"
          maxWidth="1200px"
          margin="0 auto"
        >
          {/* Feature Card 1 */}
          <Card backgroundColor="#1A1A1A" borderRadius="medium" className="feature-card">
            <Flex direction="column" alignItems="center" padding="2rem">
              <View
                width="4rem"
                height="4rem"
                backgroundColor="rgba(59, 130, 246, 0.2)"
                borderRadius="50%"
                className="icon-container"
                marginBottom="1.5rem"
              >
                <Shield size={24} color="#3B82F6" className="feature-icon" />
              </View>
              <Heading level={3} color="white" fontWeight="bold" marginBottom="1rem" textAlign="center">
                Your Memories, Your Control
              </Heading>
              <Text color="#AAAAAA" textAlign="center">
                Share photos exclusively with those who were there. No public feeds, no data mining, no privacy concerns.
              </Text>
            </Flex>
          </Card>

          {/* Feature Card 2 */}
          <Card backgroundColor="#1A1A1A" borderRadius="medium" className="feature-card">
            <Flex direction="column" alignItems="center" padding="2rem">
              <View
                width="4rem"
                height="4rem"
                backgroundColor="rgba(59, 130, 246, 0.2)"
                borderRadius="50%"
                className="icon-container"
                marginBottom="1.5rem"
              >
                <Users size={24} color="#3B82F6" className="feature-icon" />
              </View>
              <Heading level={3} color="white" fontWeight="bold" marginBottom="1rem" textAlign="center">
                Everyone Contributes
              </Heading>
              <Text color="#AAAAAA" textAlign="center">
                Invite companions to add their photos, creating a complete collaborative story of your event together.
              </Text>
            </Flex>
          </Card>

          {/* Feature Card 3 */}
          <Card backgroundColor="#1A1A1A" borderRadius="medium" className="feature-card">
            <Flex direction="column" alignItems="center" padding="2rem">
              <View
                width="4rem"
                height="4rem"
                backgroundColor="rgba(59, 130, 246, 0.2)"
                borderRadius="50%"
                className="icon-container"
                marginBottom="1.5rem"
              >
                <Film size={24} color="#3B82F6" className="feature-icon" />
              </View>
              <Heading level={3} color="white" fontWeight="bold" marginBottom="1rem" textAlign="center">
                Beautiful Organization
              </Heading>
              <Text color="#AAAAAA" textAlign="center">
                Each album becomes a beautiful collection, arranged for easy reminiscing anytime.
              </Text>
            </Flex>
          </Card>
        </Grid>
      </View>

      {/* App Preview Section */}
      <View className="preview-section">
        <Flex
          direction={{ base: 'column', large: 'row' }}
          alignItems="center"
          maxWidth="1200px"
          margin="0 auto"
          gap="3rem"
        >
          <Flex
            direction="column"
            flex="1"
            marginBottom={{ base: '2rem', large: '0' }}
          >
            <Heading level={2} color="white" fontWeight="bold" marginBottom="1rem">
              Experience Your Travels in a New Light
            </Heading>
            <View width="6rem" height="0.25rem" backgroundColor="#3B82F6" marginBottom="2rem" />
            <Text color="white" fontSize="1.125rem" marginBottom="2rem">
              Banjara transforms your travel and event photos into beautiful memories. Create albums, invite friends, and build a private collection that's perfectly organized.
            </Text>

            <Flex direction="column" gap="1.5rem">
              <Flex alignItems="flex-start" gap="1rem">
                <View
                  width="2.5rem"
                  height="2.5rem"
                  backgroundColor="rgba(59, 130, 246, 0.2)"
                  borderRadius="50%"
                  className="icon-container"
                  style={{ flexShrink: "0" }}
                >
                  <Shield size={18} color="#3B82F6" />
                </View>
                <Flex direction="column">
                  <Heading level={4} color="white" fontWeight="bold" marginBottom="0.5rem">
                    End-to-End Privacy
                  </Heading>
                  <Text color="#AAAAAA">
                    Your photos stay between you and those you explicitly invite.
                  </Text>
                </Flex>
              </Flex>

              <Flex alignItems="flex-start" gap="1rem">
                <View
                  width="2.5rem"
                  height="2.5rem"
                  backgroundColor="rgba(59, 130, 246, 0.2)"
                  borderRadius="50%"
                  className="icon-container"
                  style={{ flexShrink: "0" }}
                >
                  <Camera size={18} color="#3B82F6" />
                </View>
                <Flex direction="column">
                  <Heading level={4} color="white" fontWeight="bold" marginBottom="0.5rem">
                    Timeline Organization
                  </Heading>
                  <Text color="#AAAAAA">
                    Photos automatically organized by date and location.
                  </Text>
                </Flex>
              </Flex>

              <Flex alignItems="flex-start" gap="1rem">
                <View
                  width="2.5rem"
                  height="2.5rem"
                  backgroundColor="rgba(59, 130, 246, 0.2)"
                  borderRadius="50%"
                  className="icon-container"
                  style={{ flexShrink: "0" }}
                >
                  <Users size={18} color="#3B82F6" />
                </View>
                <Flex direction="column">
                  <Heading level={4} color="white" fontWeight="bold" marginBottom="0.5rem">
                    Role-Based Access
                  </Heading>
                  <Text color="#AAAAAA">
                    Customize what each companion can view or contribute.
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          </Flex>

          <View flex="1" className="phone-mockup-container">
            <View className="phone-mockup">
              <View className="phone-header"></View>
              <View className="phone-content">
                <Flex justifyContent="space-between" alignItems="center" marginBottom="1rem">
                  <Text fontWeight="bold" color="white" fontSize="1.25rem">My Albums</Text>
                  <View
                    width="2rem"
                    height="2rem"
                    backgroundColor="#3B82F6"
                    borderRadius="50%"
                    className="icon-container"
                  >
                    <Camera size={16} color="white" />
                  </View>
                </Flex>

                <Flex direction="column" gap="1rem" className="trip-cards">
                  {/* Trip Card 1 */}
                  <View className="trip-card" style={{ position: 'relative', height: '130px' }}>
                    <View className="trip-card-overlay"></View>
                    <View style={{ position: 'absolute', bottom: 0, padding: '0.75rem' }}>
                      <Text fontWeight="bold" color="white">Norway</Text>
                      <Text fontSize="0.75rem" color="rgba(255,255,255,0.8)">24 photos • 3 members</Text>
                    </View>
                  </View>

                  {/* Trip Card 2 */}
                  <View className="trip-card trip-card-2" style={{ position: 'relative', height: '130px' }}>
                    <View className="trip-card-overlay"></View>
                    <View style={{ position: 'absolute', bottom: 0, padding: '0.75rem' }}>
                      <Text fontWeight="bold" color="white">Morocco</Text>
                      <Text fontSize="0.75rem" color="rgba(255,255,255,0.8)">36 photos • 2 members</Text>
                    </View>
                  </View>

                  {/* Trip Card 3 */}
                  <View className="trip-card trip-card-3" style={{ position: 'relative', height: '130px' }}>
                    <View className="trip-card-overlay"></View>
                    <View style={{ position: 'absolute', bottom: 0, padding: '0.75rem' }}>
                      <Text fontWeight="bold" color="white">Hawaii</Text>
                      <Text fontSize="0.75rem" color="rgba(255,255,255,0.8)">18 photos • 4 members</Text>
                    </View>
                  </View>
                </Flex>
              </View>
            </View>
          </View>
        </Flex>
      </View>

      {/* Final CTA Section */}
      <View className="cta-section">
        <Flex
          direction="column"
          alignItems="center"
          textAlign="center"
          maxWidth="800px"
          margin="0 auto"
        >
          <Heading level={2} color="white" fontWeight="bold" fontSize="2rem" marginBottom="1rem">
            Your Next Adventure Awaits
          </Heading>
          <Text color="#AAAAAA" fontSize="1.125rem" marginBottom="2rem">
            Start capturing memories that stay between you and your companions.
          </Text>
          <Button
            className="cta-button primary final-cta"
            size="large"
            onClick={() => navigate('/trips/new')}
          >
            Create Your First Album
          </Button>
        </Flex>
      </View>

      {/* Footer */}
      
    </View>
  );
};

export default HomePage;